"use client"

import { useState, useCallback } from "react"
import { ChevronDown, ChevronUp, Check, Copy, Layers, Table2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/use-translation"
import { useLocalizedField } from "@/utils/language-utils"
import { useViewManager } from "../view-manager-context"
import { TablePreview } from "../preview/table-preview"
import { StageColumnList } from "../panels/stage-column-list"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function ColumnsStep() {
  const { t } = useTranslation()
  const { state, dispatch } = useViewManager()
  const getLocalizedField = useLocalizedField()
  const [showApplyPopover, setShowApplyPopover] = useState(false)
  const [applyTargets, setApplyTargets] = useState<string[]>([])
  const showPreview = state.showPreview
  const setShowPreview = (v: boolean) => dispatch({ type: 'SET_SHOW_PREVIEW', payload: v })

  const stages = state.originalConfig?.stageConfigurations || []
  const activeStage = state.activeEditStage

  if (state.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F214B]" />
        <span className="text-sm">{t('loading') || 'Loading...'}</span>
      </div>
    )
  }

  if (!state.selectedView) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        {t('vmSelectViewFirst' as any) || 'Select a view from the Views tab to configure columns'}
      </div>
    )
  }

  const handleApplyToStages = () => {
    if (applyTargets.length === 0) return
    dispatch({ type: 'APPLY_TO_STAGES', payload: { sourceStageId: activeStage, targetStageIds: applyTargets } })
    setShowApplyPopover(false)
    setApplyTargets([])
  }

  const toggleApplyTarget = (stageId: string) => {
    setApplyTargets(prev =>
      prev.includes(stageId) ? prev.filter(id => id !== stageId) : [...prev, stageId]
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left: Stage Selector ── */}
      <div className="w-52 border-r border-gray-200 bg-gradient-to-b from-[#F4F3FF] to-[#FAFAFF] flex-shrink-0 flex flex-col">
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <Layers size={16} className="text-[#0F214B]" />
            <h3 className="text-sm font-bold text-[#0F214B]">
              {t('stages' as any) || 'Stages'}
            </h3>
          </div>
          <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
            {t('vmStageColumnsHelp' as any) || 'Select a stage to preview and reorder its columns. Use the grid below to toggle column visibility across stages.'}
          </p>
          <div className="space-y-1.5">
            {stages.map((stage: any) => {
              const stageId = stage.stageId || stage.stageCode
              const title = getLocalizedField(stage, 'title') || stage.stageCode
              const colCount = (state.draftStageColumns[stageId] || []).length
              const isActive = activeStage === stageId
              return (
                <button
                  key={stageId}
                  onClick={() => dispatch({ type: 'SET_ACTIVE_STAGE', payload: stageId })}
                  className={`
                    w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-between
                    ${isActive
                      ? 'bg-white text-[#0F214B] shadow-md border border-blue-200 ring-1 ring-blue-100'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white/60 border border-transparent'
                    }
                  `}
                >
                  <span className="truncate">{title}</span>
                  <span className={`
                    text-xs px-1.5 py-0.5 rounded-full flex-shrink-0
                    ${isActive ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-gray-100 text-gray-400'}
                  `}>
                    {colCount}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Apply to stages */}
        <div className="p-4 border-t border-gray-200/60">
          <Popover open={showApplyPopover} onOpenChange={setShowApplyPopover}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full flex items-center justify-center gap-2 text-xs"
              >
                <Copy size={14} />
                {t('vmApplyToStages' as any) || 'Apply to stages'}
                <ChevronDown size={12} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="start">
              <p className="text-xs text-gray-500 mb-2 px-1">
                {t('vmCopyLayoutTo' as any) || 'Copy current stage layout to:'}
              </p>
              {stages.map((stage: any) => {
                const stageId = stage.stageId || stage.stageCode
                if (stageId === activeStage) return null
                const title = getLocalizedField(stage, 'title') || stage.stageCode
                const isChecked = applyTargets.includes(stageId)
                return (
                  <button
                    key={stageId}
                    onClick={() => toggleApplyTarget(stageId)}
                    role="checkbox"
                    aria-checked={isChecked}
                    aria-label={title}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-gray-50 text-left"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isChecked ? 'bg-[#0F214B] border-[#0F214B] text-white' : 'border-gray-300'
                    }`}>
                      {isChecked && <Check size={10} strokeWidth={3} />}
                    </div>
                    <span className="text-gray-700">{title}</span>
                  </button>
                )
              })}
              <Button
                variant="elliptical"
                size="sm"
                onClick={handleApplyToStages}
                disabled={applyTargets.length === 0}
                className="w-full mt-2 text-xs"
              >
                {t('apply') || 'Apply'} ({applyTargets.length})
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Right: Preview + Column Grid ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Table preview (collapsible, full width) */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 text-sm font-semibold text-[#0F214B] hover:text-blue-700 transition-colors"
            >
              {showPreview ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <Table2 size={15} />
              {t('vmColumnPreview' as any) || 'Table Preview'}
              <span className="font-normal text-gray-400 text-xs">
                — {getLocalizedField(stages.find((s: any) => (s.stageId || s.stageCode) === activeStage) || {}, 'title') || activeStage}
              </span>
            </button>
          </div>
          {showPreview && (
            <div>
              <TablePreview />
            </div>
          )}
        </div>

        {/* Side-by-side stage comparison grid */}
        <div className="flex-1 overflow-hidden border-t border-gray-200">
          <StageColumnList />
        </div>
      </div>
    </div>
  )
}
